// Thunks relacionados con notificaciones
import { createAsyncThunk } from "@reduxjs/toolkit";
import api from "@/services/api";
import {
  notificacionesSuccess,
  notificacionesFailure,
  appendNotifications,
  unreadCountSuccess,
  unreadCountFailure,
  markAsReadSuccess,
  markAsReadFailure,
  createNotificacionSuccess,
  createNotificacionFailure,
} from "./notificacionSlice";

/**
 * 📝 NOTIFICACIÓN — Obtener mis notificaciones
 * Obtiene las notificaciones del usuario autenticado con filtros y paginación
 * 
 * @param {Object} params - Parámetros de consulta
 * @param {boolean} params.read - Filtrar por leídas/no leídas
 * @param {string} params.type - Tipo de notificación ('any')
 * @param {string} params.date - Filtro de fecha ('hoy'|'anterior')
 * @param {number} params.page - Número de página (default: 1)
 * @param {number} params.limit - Cantidad por página (default: 20)
 * @param {boolean} params.append - Si es true, agrega a las existentes en lugar de reemplazar
 */
export const fetchMyNotificaciones = createAsyncThunk(
  "notificacion/fetchMyNotificaciones",
  async (params = {}, { dispatch, rejectWithValue }) => {
    try {
      const { append = false, ...queryParams } = params;
      const urlParams = new URLSearchParams();
      
      if (queryParams.read !== undefined) {
        urlParams.append('read', queryParams.read);
      }
      if (queryParams.type) {
        urlParams.append('type', queryParams.type);
      }
      if (queryParams.date) {
        urlParams.append('date', queryParams.date);
      }
      urlParams.append('page', queryParams.page || 1);
      urlParams.append('limit', queryParams.limit || 20);

      const { data } = await api.get(`/notificaciones?${urlParams.toString()}`);
      
      // Despachar según si es append o reemplazo
      if (append) {
        dispatch(appendNotifications(data));
      } else {
        dispatch(notificacionesSuccess(data));
      }
      
      return data;
    } catch (err) {
      console.error("Error al obtener notificaciones:", err);
      const errorMsg = err.response?.data?.message || "Error al obtener notificaciones";
      dispatch(notificacionesFailure(errorMsg));
      return rejectWithValue(errorMsg);
    }
  }
);

/**
 * 📝 NOTIFICACIÓN — Obtener conteo de notificaciones no leídas
 * Obtiene el número de notificaciones sin leer para mostrar en badge
 */
export const fetchUnreadCount = createAsyncThunk(
  "notificacion/fetchUnreadCount",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await api.get('/notificaciones/unread-count');
      
      dispatch(unreadCountSuccess(data.unreadCount));
      return data.unreadCount;
    } catch (err) {
      console.error("Error al obtener conteo de no leídas:", err);
      const errorMsg = err.response?.data?.message || "Error al obtener conteo";
      dispatch(unreadCountFailure(errorMsg));
      return rejectWithValue(errorMsg);
    }
  }
);

/**
 * 📝 NOTIFICACIÓN — Marcar notificación como leída
 * Marca una notificación específica como leída
 * 
 * @param {number} notificationId - ID de la notificación a marcar como leída
 */
export const markNotificationAsRead = createAsyncThunk(
  "notificacion/markAsRead",
  async (notificationId, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await api.put(`/notificaciones/${notificationId}/leer`);
      
      dispatch(markAsReadSuccess(notificationId));
      // Actualizar el conteo de no leídas después de marcar como leída
      dispatch(fetchUnreadCount());
      
      return data;
    } catch (err) {
      console.error("Error al marcar notificación como leída:", err);
      const errorMsg = err.response?.data?.message || "Error al marcar como leída";
      dispatch(markAsReadFailure(errorMsg));
      return rejectWithValue(errorMsg);
    }
  }
);

/**
 * 📝 NOTIFICACIÓN — Crear notificaciones
 * Crea notificaciones para múltiples usuarios
 * 
 * @param {Object} notificationData - Datos de la notificación
 * @param {string} notificationData.urlDestino - URL de destino
 * @param {string} notificationData.type - Tipo ('any')
 * @param {string} notificationData.emisor - Quien emite la notificación
 * @param {string} notificationData.contenido - Contenido de la notificación
 * @param {string[]} notificationData.userUids - Array de UIDs de usuarios a notificar
 */
export const createNotificacion = createAsyncThunk(
  "notificacion/create",
  async (notificationData, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await api.post('/notificaciones', notificationData);
      
      dispatch(createNotificacionSuccess(data));
      return data;
    } catch (err) {
      console.error("Error al crear notificación:", err);
      const errorMsg = err.response?.data || err.response?.data?.message || "Error al crear notificación";
      dispatch(createNotificacionFailure(errorMsg));
      return rejectWithValue(errorMsg);
    }
  }
);

/**
 * 📝 EMPRESA — Actualizar información de una empresa
 */
export const updateInfoEmpresa = createAsyncThunk(
  "empresa/updateInfoEmpresa",
  async ({ empresaUid, formData }, { dispatch, getState, rejectWithValue }) => {
    try {
      const response = await api.put(`/empresas/${empresaUid}`, formData);
      
      // Merge the updated data with existing state
      const currentEmpresa = getState().empresa.infoEmpresa;
      const updatedEmpresa = {
        ...currentEmpresa,
        ...response.data.empresa
      };
      
      dispatch(empresaSuccess({ infoEmpresa: updatedEmpresa }));
      return response.data;
    } catch (error) {
      dispatch(empresaFailure(error.response?.data?.message || "Error al actualizar la empresa"));
      return rejectWithValue(
        error.response?.data?.message || "Error al actualizar la empresa"
      );
    }
  }
);
